import styles from "./StepTwo.module.css";
import { v4 as uuidv4 } from "uuid";
import { useFormik } from "formik";
import * as Yup from "yup";
import Icon from "../../../components/Icon/Icon";
import { useState } from "react";
import CreateDocumentTypeKeywordsModal from "../../../components/CreateDocumentTypeKeywordsModal/CreateDocumentTypeKeywordsModal";

import api from "../../../api/api";
import type { AnalyzeKeywordsResponseType } from "../../../shared/types/files.types";
import { normalizeAnalysisResponse } from "../../../shared/types/files.types";
import type { GetTemplateResultSuccessType } from "../../../shared/types/files.types";

const validationSchema = Yup.object({
  keywords: Yup.array()
    .of(Yup.object({ id: Yup.string(), value: Yup.string() }))
    .min(1, "No pueden faltar palabras clave!"),
});

type submitValuesType = {
  keywords: { id: string; value: string }[];
  text: string;
};

type StepTwoProps = {
  onSubmit: (values: submitValuesType) => void;
  extractedData: GetTemplateResultSuccessType;
  savedData: submitValuesType | null;
  getBack: () => void;
};

const StepTwo = ({
  onSubmit,
  extractedData,
  getBack,
  savedData,
}: StepTwoProps) => {
  const [selectedKeywords, setSelectedKeywords] = useState<string[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [showDiscovery, setShowDiscovery] = useState(true);
  const [showConflicts, setShowConflicts] = useState(true);
  const initialValues =
    savedData === null
      ? {
          keywords: [],
          text: extractedData.text,
        }
      : {
          ...savedData,
        };

  const formik = useFormik<submitValuesType>({
    initialValues,
    validationSchema,
    onSubmit: (values) => {
      onSubmit(values);
    },
  });

  const { values, errors, handleSubmit, setFieldValue } = formik;

  const toggleKeyword = (id: string) => {
    setSelectedKeywords((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const addKeyword = (value: string) => {
    const newKeyword = { value, id: uuidv4() };
    setFieldValue("keywords", [newKeyword, ...values.keywords]);
  };

  const deleteKeyword = (id: string) => {
    const updated = values.keywords.filter((k) => k.id !== id);
    setFieldValue("keywords", updated);
    setSelectedKeywords((prev) => prev.filter((keyId) => keyId !== id));
  };

  const deleteSelectedKeywords = () => {
    const updated = values.keywords.filter(
      ({ id }) => !selectedKeywords.includes(id)
    );
    setFieldValue("keywords", updated);
    setSelectedKeywords([]);
  };

  const [analyzedData, setAnalyzedData] =
    useState<AnalyzeKeywordsResponseType | null>(null);

  const analyzeKeywords = async () => {
    const mappedKeywords = values.keywords.map((keyword) => keyword.value);
    const response = await api.analyzeKeywords({
      text: extractedData.text,
      keywords: mappedKeywords,
    });
    setAnalyzedData(response);
  };

  // Categorize results for display
  const categorizedResults = analyzedData
    ? normalizeAnalysisResponse(analyzedData)
    : null;

  const ICONS = {
    test: "document-verify" as const,
    conflict: "document-failure" as const,
    discovery: "stars" as const,
    chevronUp: "select-arrow-to-bot" as const,
    chevronDown: "get-back-arrow" as const,
    info: "document-analyze" as const,
  };

  const renderResultSection = (
    title: string,
    results: AnalyzeKeywordsResponseType,
    description: string,
    icon: keyof typeof ICONS,
    colorClass: string,
    isVisible: boolean,
    toggleVisibility?: () => void
  ) => {
    if (results.length === 0) return null;
    return (
      <div className={`${styles.observationSection} ${styles[colorClass]}`}>
        <div className={styles.observationSectionHeader}>
          <div className={styles.observationSectionTitle}>
            <Icon name={ICONS[icon]} size={20} />
            <h4>{title}</h4>
            {toggleVisibility && (
              <button
                type="button"
                className={styles.toggleButton}
                onClick={toggleVisibility}
              >
                <Icon
                  name={isVisible ? ICONS.chevronUp : ICONS.chevronDown}
                  size={16}
                />
              </button>
            )}
          </div>
          <p className={styles.observationSectionDescription}>{description}</p>
        </div>
        {isVisible && (
          <ul className={styles.observationsList}>
            {results.map((message) => (
              <li
                key={message.documentTypeId}
                className={`${styles.observationsListItem} ${
                  styles[colorClass + "Item"]
                }`}
              >
                <p className={styles.observationParagraph}>
                  <span className={styles.observationName}>
                    Tipo de plantilla:{" "}
                  </span>
                  {message.documentTypeId === "base-template" ||
                  message.documentTypeId === "1"
                    ? "Sus palabras clave (Prueba)"
                    : message.documentTypeName}
                </p>
                <p className={styles.observationParagraph}>
                  <span className={styles.observationName}>
                    Palabras clave encontradas:{" "}
                  </span>
                  {message.matchedKeywords.length > 0
                    ? message.matchedKeywords.join(", ").toLowerCase()
                    : "Ninguna"}
                </p>
                <p className={styles.observationParagraph}>
                  <span className={styles.observationName}>Precisión:</span>{" "}
                  {Math.round(message.confidence * 100)}%
                </p>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  };

  return (
    <>
      <form className={styles.form} onSubmit={handleSubmit}>
        <div className={styles.rightSideContainer}>
          <p className={styles.title}>Texto del documento</p>
          <div className={styles.extractedTextOuter}>
            <div className={styles.extractedTextScroll}>
              <p className={styles.extractedTextInner}>{extractedData.text}</p>
            </div>
          </div>
          <div className={styles.observationsContainer}>
            <p className={styles.title}>Análisis de palabras clave</p>
            {analyzedData === null ? (
              <div className={styles.analysisPlaceholder}>
                <Icon name="document-analyze" size={48} />
                <p>
                  Pulse "Analizar palabras" para obtener resultados del
                  análisis.
                </p>
              </div>
            ) : analyzedData.length === 0 ? (
              <div className={styles.analysisEmpty}>
                <Icon name="document-failure" size={48} />
                <p>No se encontraron coincidencias en el análisis.</p>
              </div>
            ) : (
              <div className={styles.analysisResults}>
                {categorizedResults &&
                  renderResultSection(
                    "Resultados de sus palabras clave",
                    categorizedResults.testResults,
                    "Verificación de si sus palabras clave aparecen en el documento",
                    "test",
                    "testSection",
                    true
                  )}
                {categorizedResults &&
                  renderResultSection(
                    "Conflictos detectados",
                    categorizedResults.conflictResults,
                    "Sus palabras clave ya existen en estas plantillas. Considere usar una plantilla existente.",
                    "conflict",
                    "conflictSection",
                    showConflicts,
                    () => setShowConflicts(!showConflicts)
                  )}
                {categorizedResults &&
                  renderResultSection(
                    "Sugerencias inteligentes",
                    categorizedResults.discoveryResults,
                    "Plantillas existentes que podrían coincidir con su documento basándose en el contenido del texto",
                    "discovery",
                    "discoverySection",
                    showDiscovery,
                    () => setShowDiscovery(!showDiscovery)
                  )}
                <div className={styles.infoBox}>
                  <Icon name={ICONS.info} size={20} />
                  <div>
                    <p>
                      <strong>¿Qué significan estos resultados?</strong>
                    </p>
                    <ul>
                      <li>
                        <strong>Resultados de prueba:</strong> Muestran qué tan
                        bien funcionan sus palabras clave
                      </li>
                      <li>
                        <strong>Conflictos:</strong> Palabras clave que ya
                        existen en otras plantillas
                      </li>
                      <li>
                        <strong>Sugerencias:</strong> Plantillas que ya podrían
                        manejar este tipo de documento
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className={styles.leftSideContainer}>
          <div className={styles.keywordsContainer}>
            <p className={styles.title}>Palabras clave seleccionadas</p>
            <button
              type="button"
              className={styles.deleteKeywordButton}
              onClick={deleteSelectedKeywords}
              disabled={selectedKeywords.length === 0}
            >
              Eliminar Palabra <Icon size={20} name="document-failure" />
            </button>

            {errors.keywords && typeof errors.keywords === "string" && (
              <p className={styles.textWarning}>{errors.keywords}</p>
            )}

            <ul className={styles.keywordsList}>
              {values.keywords.map(({ id, value }) => (
                <li key={id} className={styles.keywordsListItem}>
                  <input
                    type="checkbox"
                    id={`checkbox-${id}`}
                    className={styles.customCheckbox}
                    checked={selectedKeywords.includes(id)}
                    onChange={() => toggleKeyword(id)}
                  />
                  <label
                    htmlFor={`checkbox-${id}`}
                    className={styles.customCheckboxLabel}
                  >
                    {value}
                  </label>
                </li>
              ))}
            </ul>

            <div className={styles.keywordsActionsContainer}>
              <button
                type="button"
                className={styles.gradientButton}
                onClick={() => {
                  setIsOpen(true);
                  setSelectedKeywords([]);
                }}
              >
                Añadir nueva <Icon name="edit-pen" size={22} gradient />
              </button>
              <span className={styles.divider}></span>
              <button
                type="button"
                className={styles.gradientButton}
                disabled={values.keywords.length === 0}
                onClick={analyzeKeywords}
              >
                Analizar palabras{" "}
                <Icon name="document-analyze" size={22} gradient />
              </button>
            </div>
          </div>

          <div className={styles.formActionsButtonsContainer}>
            <button
              onClick={getBack}
              type="button"
              className={styles.cancelButton}
            >
              Volver
            </button>
            <button type="submit" className={styles.submitButton}>
              Siguiente
            </button>
          </div>
        </div>
      </form>

      <CreateDocumentTypeKeywordsModal
        isOpen={isOpen}
        onRequestClose={() => setIsOpen(false)}
        onCreate={addKeyword}
        keywords={values.keywords}
        deleteKeyword={deleteKeyword}
      />
    </>
  );
};

export default StepTwo;
